import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Popper, Fade, Paper, TextField, Grid, Button, Typography, Box } from "@mui/material";
import { createInputHandler } from "features/planner/utils/inputValidation";

function ExerciseEditModal({ open, anchorEl, onClose, onSave, slot }) {
  const [form, setForm] = useState({
    sets: "",
    reps: "",
    rest: "",
    intensity: "",
    tempo: "",
    notes: "",
  });

  // Cargar datos al abrir
  useEffect(() => {
    if (slot && open) {
      setForm({
        sets: slot.sets || "",
        reps: slot.reps || "",
        rest: slot.rest || "",
        intensity: slot.intensity || "",
        tempo: slot.tempo || "",
        notes: slot.notes || "",
      });
    }
  }, [slot, open]);

  const handleSave = () => onSave(form);

  if (!slot) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top"
      transition
      modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      style={{ zIndex: 3000 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={140}>
          <Paper
            elevation={0}
            sx={{
              width: 230,
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "grey.300",
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                backgroundColor: "grey.100",
                px: 1.6,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "grey.300",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Editar</Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                }}
              >
                {slot.name}
              </Typography>
            </Box>

            {/* BODY */}
            <Box sx={{ px: 1.6, py: 1.2 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    label="Series"
                    fullWidth
                    size="small"
                    value={form.sets}
                    onChange={createInputHandler("sets", setForm)}
                    inputProps={{ maxLength: 2 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Reps"
                    fullWidth
                    size="small"
                    value={form.reps}
                    onChange={createInputHandler("reps", setForm)}
                    inputProps={{ maxLength: 5 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Descanso"
                    fullWidth
                    size="small"
                    value={form.rest}
                    onChange={createInputHandler("rest", setForm)}
                    inputProps={{ maxLength: 3 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Intensidad"
                    fullWidth
                    size="small"
                    value={form.intensity}
                    onChange={createInputHandler("intensity", setForm)}
                    inputProps={{ maxLength: 8 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Tempo"
                    fullWidth
                    size="small"
                    value={form.tempo}
                    onChange={createInputHandler("tempo", setForm)}
                    inputProps={{ maxLength: 8 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    placeholder="3-1-1-0"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Nota"
                    fullWidth
                    size="small"
                    value={form.notes}
                    onChange={createInputHandler("notes", setForm)}
                    inputProps={{ maxLength: 15 }}
                    InputProps={{ sx: { height: 34, fontSize: "0.8rem" } }}
                    InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
                    placeholder="dropset, tempo..."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* FOOTER */}
            <Box
              sx={{
                px: 1.6,
                py: 1.2,
                display: "flex",
                justifyContent: "center",
                gap: 1.2,
                backgroundColor: "grey.100",
                borderTop: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.2,
                  color: "secondary.main",
                  borderColor: "grey.300",
                  "&:hover": {
                    borderColor: "secondary.main",
                    backgroundColor: "background.paper",
                  },
                }}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "6px",
                  fontSize: "0.75rem",
                  px: 1.8,
                  py: 0.2,
                  color: "white.main",
                  backgroundColor: "primary.main",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "primary.focus",
                  },
                }}
              >
                Guardar
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
}

ExerciseEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  slot: PropTypes.object,
};

export default ExerciseEditModal;
